import { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Spinner, Badge, Alert } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { customerApi } from '../contexts/AuthContext';
import { getApiUrl, API_BASE_URL } from '../config/api';
// New packages
import { ProgressBar } from '@veeyaainnovatives/progress-bar';
import { Accordion, AccordionItem, AccordionHeader, AccordionBody } from '@veeyaainnovatives/accordion';

const CustomerProjectPlanner = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { project_id } = useParams();
    const [planId, setPlanId] = useState(null);
    const [planDetails, setPlanDetails] = useState(null);
    const [steps, setSteps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const hasFetched = useRef(false);
    

    useEffect(() => {
        // Get project_id from URL params
        const projectIdFromUrl = project_id ? parseInt(project_id) : null;
        
        if (!projectIdFromUrl || isNaN(projectIdFromUrl)) {
            setError('Invalid project ID. Please select a project from the dashboard.');
            setLoading(false);
            return;
        }
        
        // Prevent duplicate calls - check if we already fetched this project
        if (hasFetched.current && planId === projectIdFromUrl) {
            return;
        }
        
        // Reset hasFetched when project_id changes
        if (planId !== projectIdFromUrl) {
            hasFetched.current = false;
            setPlanId(projectIdFromUrl);
        }
        
        const fetchProjectDetails = async () => {
            try {
                hasFetched.current = true;
                setLoading(true);
                setError('');
                
                // Fetch project details from API using project_id from URL
                const response = await customerApi.get(
                    getApiUrl(`api/customer/projects/${projectIdFromUrl}`)
                );
                
                if (response.data.success && response.data.data) {
                    const project = response.data.data;
                    
                    // Map API response to match UI structure
                    const mappedPlanDetails = {
                        id: project.id,
                        plan_title: project.name,
                        status: 'active',
                        start_date: project.start_date,
                        end_date: project.end_date,
                        description: project.description,
                        customer_id: project.customer_id,
                        // Use progress from API response, fallback to 0
                        total_progress: project.progress !== undefined ? project.progress : 0,
                        subscription: project.subscription ? {
                            title: project.subscription.title || '',
                            short_description: project.subscription.short_description || '',
                            main_description: project.subscription.main_description || project.description || '',
                            start_price: project.subscription.start_price || '0',
                            end_price: project.subscription.end_price || '0',
                            price_unit: project.subscription.price_unit || 'month'
                        } : null
                    };
                    
                    // Map steps from API response
                    // API returns stepDocuments array, fallback to documents if stepDocuments not available
                    const mappedSteps = (project.steps || []).map((step, index) => {
                        // Use stepDocuments if available, otherwise use documents
                        const stepDocs = step.stepDocuments || step.documents || [];
                        return {
                            id: step.id || index + 1,
                            step_number: step.step_id || index + 2, // step_id from API
                            step_name: step.name || step.step_name || null, // Step name from API
                            notes: step.description || 'No notes available.',
                            status: step.status === 'pending' ? 'not_started' : (step.status || 'not_started'), // Map 'pending' to 'not_started'
                            completion_date: step.completion_date || null,
                            documents: stepDocs.map((doc, docIndex) => {
                                // Use document ID from API if available
                                const docId = doc.id || (docIndex + 1);
                                // Get file_name for document URL construction
                                const fileName = doc.file_name || doc.name || '';
                                return {
                                    id: docId,
                                    name: doc.name || doc.file_name || 'Document',
                                    file_name: doc.file_name || doc.name || fileName,
                                    file_url: doc.file_url || null, // Store file_url if available
                                    file_path: doc.file_path || null, // Store file_path if available
                                    url: doc.file_url || doc.file_path, // For backward compatibility
                                    created_at: doc.created_at || step.created_at || step.updated_at || null
                                };
                            })
                        };
                    });
                    
                    setPlanDetails(mappedPlanDetails);
                    setSteps(mappedSteps);
                } else {
                    setError('Failed to load project details. Please try again.');
                }
            } catch (err) {
                console.error('Error fetching project details:', err);
                hasFetched.current = false; // Reset on error so it can retry
                if (err.response) {
                    const { status, data } = err.response;
                    if (status === 401) {
                        setError('Session expired. Please login again.');
                    } else if (status === 404) {
                        setError('Project not found.');
                    } else {
                        setError(data?.message || 'Failed to load project details. Please try again.');
                    }
                } else if (err.request) {
                    setError('Network error. Please check your connection.');
                } else {
                    setError('An error occurred. Please try again.');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchProjectDetails();
    }, [project_id]);


    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const statusLower = (status || '').toLowerCase();
        if (statusLower === 'completed' || statusLower === 'done') {
            return <Badge bg="success">Completed</Badge>;
        } else if (statusLower === 'in_progress' || statusLower === 'in-progress') {
            return <Badge bg="warning">In Progress</Badge>;
        } else if (statusLower === 'not_started' || statusLower === 'not-started' || statusLower === 'pending') {
            return <Badge bg="secondary">Not Started</Badge>;
        }
        return <Badge bg="info">{status || 'Unknown'}</Badge>;
    };

    // Get progress from API/static data
    const getProgress = () => {
        return planDetails?.total_progress || 0;
    };

    const isPdfFile = (fileName) => {
        if (!fileName) return false;
        return fileName.toLowerCase().endsWith('.pdf');
    };

    const isImageFile = (fileName) => {
        if (!fileName) return false;
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
        return imageExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
    };

    // Fetch file as blob to avoid CORS issues
    // Get document viewer URL with token and document_id
    // URL format: /view-document?token={access_token}&document_id={document_id}
    const getDocumentViewerUrl = (documentId) => {
        if (!documentId) {
            console.error('Document ID is required');
            return '';
        }
        
        // Get access token from localStorage
        const accessToken = localStorage.getItem('customer_access_token');
        
        if (!accessToken) {
            console.error('Access token not found. Please login again.');
            throw new Error('Access token not found. Please login again.');
        }
        
        // Construct the document viewer URL
        // Use document.id (from project_step_documents table) as document_id
        const viewerUrl = `${API_BASE_URL}/serve-document?token=${encodeURIComponent(accessToken)}&document_id=${encodeURIComponent(documentId)}`;
        
        return viewerUrl;
    };

    // Get file URL for display (used for thumbnails/previews)
    // This is synchronous and used for image src attributes
    const getFileViewUrl = (doc) => {
        // For thumbnails, use the file_url directly if available
        // The full viewer will use blob URLs
        if (doc.file_url) {
            return doc.file_url;
        }
        if (doc.url) {
            return doc.url;
        }
        if (doc.file_path) {
            return doc.file_path;
        }
        return '';
    };

    // Handle opening file in document viewer (Laravel viewer)
    const handleOpenFile = (doc) => {
        try {
            const fileName = doc.name || doc.file_name || 'Document';
            
            // Get document ID (from project_step_documents table)
            const documentId = doc.id;
            
            if (!documentId) {
                alert('Unable to open document. Document ID is not available.');
                return;
            }
            
            // Get document viewer URL
            const viewerUrl = getDocumentViewerUrl(documentId);
            
            if (!viewerUrl) {
                alert('Unable to open document. Please try again.');
                return;
            }
            
            // Open in new tab
            window.open(viewerUrl, '_blank', 'noopener,noreferrer');
        } catch (error) {
            console.error('Error opening document:', error);
            if (error.message && error.message.includes('token')) {
                alert('Please login again to view documents.');
            } else {
                alert('Error opening document. Please try again.');
            }
        }
    };
    

    if (loading) {
        return (
            <Container className="py-5">
                <Row>
                    <Col className="text-center">
                        <Spinner animation="border" style={{ color: '#677a58' }} />
                        <p className="mt-3" style={{ color: '#6c757d' }}>Loading project details...</p>
                    </Col>
                </Row>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="py-5">
                <Row>
                    <Col>
                        <Alert variant="danger" style={{ 
                            borderRadius: '12px',
                            border: 'none',
                            backgroundColor: '#f8d7da',
                            color: '#721c24'
                        }}>
                            <Alert.Heading style={{ fontSize: '18px', marginBottom: '8px' }}>
                                Error
                            </Alert.Heading>
                            <p style={{ marginBottom: '0', fontSize: '14px' }}>{error}</p>
                        </Alert>
                        <button
                            className="btn btn-link text-decoration-none mt-3"
                            onClick={() => navigate('/customer/dashboard')}
                            style={{ 
                                paddingLeft: '0',
                                color: '#677a58',
                                fontWeight: '500'
                            }}
                        >
                            ← Back to Projects
                        </button>
                    </Col>
                </Row>
            </Container>
        );
    }

    return (
        <div style={{ backgroundColor: '#f8f9fa', minHeight: 'calc(100vh - 200px)' }}>
        <Container className="py-5">
            <Row className="mb-4">
                <Col>
                    <button
                        className="btn btn-link text-decoration-none mb-3"
                        onClick={() => navigate('/customer/dashboard')}
                        style={{ 
                            paddingLeft: '0',
                            color: '#677a58',
                            fontWeight: '500'
                        }}
                    >
                        ← Back to Projects
                    </button>
                    <h2 className="mb-3" style={{ 
                        fontWeight: 'bold',
                        fontSize: '32px',
                        color: '#677a58'
                    }}>
                        Project Planner
                    </h2>
                    {planDetails && (
                        <p className="text-muted">
                            {planDetails.plan_title || planDetails.subscription?.title || 'Project Plan'}
                        </p>
                    )}
                </Col>
            </Row>

            {/* Total Progress - Using @veeyaainnovatives/progress-bar */}
            <Row className="mb-4">
                <Col>
                    <ProgressBar
                        progress={getProgress()}
                        title="Total Progress"
                        description={`${planDetails?.total_progress || 0}% project completed`}
                        backgroundColor="#e9ecef"
                        barColor="#677a58"
                        titleColor="#677a58"
                        percentageColor="#677a58"
                        descriptionColor="#6c757d"
                    />
                </Col>
            </Row>

            {/* All Project Steps with Accordion (Including Step 1) */}
            <Row>
                <Col>
                    <h4 className="mb-4" style={{ 
                        fontWeight: 'bold',
                        fontSize: '24px',
                        color: '#677a58'
                    }}>
                        Project Steps
                    </h4>
                    
                    {steps.length === 0 ? (
                        <Card style={{ 
                            borderRadius: '16px', 
                            border: 'none',
                            backgroundColor: '#fff',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                        }}>
                            <Card.Body className="text-center py-5">
                                <p style={{ color: '#6c757d' }}>No steps available for this project yet.</p>
                            </Card.Body>
                        </Card>
                    ) : (
                        <Accordion defaultActiveKey="0" className="accordion">
                            {/* Step 1 - Project Information */}
                            <AccordionItem eventKey="step-1">
                                <AccordionHeader>
                                    <div className="w-100">
                                        <h1 className="accordian-title">
                                                            Step 1 - Project Information
                                                        </h1>
                                    </div>
                                </AccordionHeader>
                                <AccordionBody>
                                    <div className="faq-content">
                                        <Row>
                                            <Col md={6} className="mb-3">
                                                <p className="mb-2" style={{ marginBottom: '8px' }}>
                                                    <strong style={{ color: '#495057', display: 'block', marginBottom: '4px' }}>Project Name:</strong>
                                                    <span style={{ color: '#212529' }}>{planDetails?.plan_title || planDetails?.subscription?.title || 'N/A'}</span>
                                                </p>
                                                <p className="mb-2" style={{ marginBottom: '8px' }}>
                                                    <strong style={{ color: '#495057', display: 'block', marginBottom: '4px' }}>Subscription Type:</strong>
                                                    <span style={{ color: '#212529' }}>{planDetails?.subscription?.title || 'N/A'}</span>
                                                </p>
                                            </Col>
                                            <Col md={6} className="mb-3">
                                                <p className="mb-2" style={{ marginBottom: '8px' }}>
                                                    <strong style={{ color: '#495057', display: 'block', marginBottom: '4px' }}>Start Date:</strong>
                                                    <span style={{ color: '#212529' }}>{formatDate(planDetails?.start_date)}</span>
                                                </p>
                                                {planDetails?.end_date && (
                                                    <p className="mb-2" style={{ marginBottom: '8px' }}>
                                                        <strong style={{ color: '#495057', display: 'block', marginBottom: '4px' }}>End Date:</strong>
                                                        <span style={{ color: '#212529' }}>{formatDate(planDetails.end_date)}</span>
                                                    </p>
                                                )}
                                            </Col>
                                            <Col md={12}>
                                                <p className="mb-0">
                                                    <strong style={{ color: '#495057', display: 'block', marginBottom: '4px' }}>Description/Notes:</strong>
                                                    <span style={{ color: '#212529', lineHeight: '1.6' }}>
                                                        {planDetails?.description || planDetails?.subscription?.main_description || planDetails?.subscription?.short_description || 'No description available.'}
                                                    </span>
                                                </p>
                                            </Col>
                                        </Row>
                                    </div>
                                </AccordionBody>
                            </AccordionItem>

                            {/* Step 2 onwards */}
                            {steps.map((step, index) => {
                                const stepNum = step.step_number || index + 2;
                                const validDocuments = step.documents ? step.documents.filter(doc => {
                                    const fileName = doc.name || doc.file_name || '';
                                    return isPdfFile(fileName) || isImageFile(fileName);
                                }) : [];
                                
                                return (
                                    <AccordionItem 
                                        key={step.id || index} 
                                        eventKey={`step-${stepNum}`}
                                    >
                                        <AccordionHeader>
                                            <div className="w-100">
                                                <h1 className="accordian-title">
                                                    Step {stepNum}{step.step_name ? ` - ${step.step_name}` : ''} - {getStatusBadge(step.status)}
                                                </h1>
                                            </div>
                                        </AccordionHeader>
                                        <AccordionBody>
                                            <div className="faq-content">
                                                <Row>
                                                    <Col md={12} className="mb-3">
                                                        <h6 style={{ 
                                                            fontWeight: '600',
                                                            color: '#495057',
                                                            marginBottom: '12px',
                                                            fontSize: '16px'
                                                        }}>
                                                            Notes
                                                        </h6>
                                                        <p style={{ 
                                                            color: '#212529',
                                                            lineHeight: '1.6',
                                                            marginBottom: '0',
                                                            padding: '12px',
                                                            backgroundColor: '#f8f9fa',
                                                            borderRadius: '8px'
                                                        }}>
                                                            {step.notes || 'No notes available.'}
                                                        </p>
                                                    </Col>
                                                    
                                                    <Col md={6} className="mb-3">
                                                        <h6 style={{ 
                                                            fontWeight: '600',
                                                            color: '#495057',
                                                            marginBottom: '12px',
                                                            fontSize: '16px'
                                                        }}>
                                                            Status
                                                        </h6>
                                                        <div style={{ marginBottom: '16px' }}>
                                                            {getStatusBadge(step.status)}
                                                        </div>
                                                        
                                                        {step.completion_date && (
                                                            <>
                                                                <h6 style={{ 
                                                                    fontWeight: '600',
                                                                    color: '#495057',
                                                                    marginBottom: '8px',
                                                                    marginTop: '16px',
                                                                    fontSize: '16px'
                                                                }}>
                                                                    Completion Date
                                                                </h6>
                                                                <p style={{ color: '#212529', marginBottom: '0' }}>
                                                                    {formatDate(step.completion_date)}
                                                                </p>
                                                            </>
                                                        )}
                                                    </Col>
                                                    
                                                    <Col md={6}>
                                                        {/* Documents Section */}
                                                        <h6 style={{ 
                                                            fontWeight: '600',
                                                            color: '#495057',
                                                            marginBottom: '12px',
                                                            fontSize: '16px'
                                                        }}>
                                                            Documents
                                                        </h6>
                                                        {validDocuments.length > 0 ? (
                                                            <div>
                                                                {validDocuments.map((doc, docIndex) => {
                                                                    const fileName = doc.name || doc.file_name || 'Document';
                                                                    const fileViewUrl = getFileViewUrl(doc);
                                                                    const isPdf = isPdfFile(fileName);
                                                                    const isImage = isImageFile(fileName);
                                                                    
                                                                    return (
                                                                        <div 
                                                                            key={docIndex}
                                                                            style={{
                                                                                border: '1px solid #e9ecef',
                                                                                borderRadius: '8px',
                                                                                padding: '12px',
                                                                                marginBottom: docIndex < validDocuments.length - 1 ? '12px' : '0',
                                                                                backgroundColor: '#f8f9fa'
                                                                            }}
                                                                        >
                                                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                                                <div style={{ flex: 1 }}>
                                                                                    <strong style={{ color: '#212529', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                                        {isPdf && (
                                                                                            <span className="fa fa-file-pdf" style={{ color: '#dc3545', fontSize: '16px' }}></span>
                                                                                        )}
                                                                                        {isImage && (
                                                                                            <span className="fa fa-file-image" style={{ color: '#28a745', fontSize: '16px' }}></span>
                                                                                        )}
                                                                                        <span>{fileName}</span>
                                                                                    </strong>
                                                                                    {doc.created_at && (
                                                                                        <small className="text-muted" style={{ fontSize: '12px', display: 'block' }}>
                                                                                            {formatDate(doc.created_at)}
                                                                                        </small>
                                                                                    )}
                                                                                </div>
                                                                                {(isPdf || isImage) && (
                                                                                    <button
                                                                                        className="btn btn-sm"
                                                                                        onClick={() => handleOpenFile(doc)}
                                                                                        style={{
                                                                                            backgroundColor: '#677a58',
                                                                                            color: '#fff',
                                                                                            border: 'none',
                                                                                            borderRadius: '6px',
                                                                                            padding: '6px 12px',
                                                                                            fontSize: '12px',
                                                                                            fontWeight: '500',
                                                                                            marginLeft: '12px',
                                                                                            cursor: 'pointer',
                                                                                            transition: 'background-color 0.2s'
                                                                                        }}
                                                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5a6a4a'}
                                                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#677a58'}
                                                                                    >
                                                                                        {isPdf ? 'View PDF' : 'View Image'}
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        ) : (
                                                            <p style={{ color: '#6c757d', fontSize: '14px', marginBottom: '0' }}>
                                                                No PDF or image documents available for this step.
                                                            </p>
                                                        )}
                                                    </Col>
                                                </Row>
                                            </div>
                                        </AccordionBody>
                                    </AccordionItem>
                                );
                            })}
                        </Accordion>
                    )}
                </Col>
            </Row>

        </Container>
        </div>
    );
};

export default CustomerProjectPlanner;

